/**
 * English: Station list for selected org — click to set scope station.
 * Thai: รายการสถานีของ org ที่เลือก — คลิกเพื่อตั้งสถานีใน scope
 */
import { useTranslation } from 'react-i18next';
import { MapPinIcon } from '@heroicons/react/24/outline';
import type { Station } from '../../types';

interface AgentManagementStationListProps {
  orgName: string;
  stations: Station[];
  stationId: string;
  loading?: boolean;
  onStationSelect: (id: string) => void;
}

export default function AgentManagementStationList({
  orgName,
  stations,
  stationId,
  loading = false,
  onStationSelect,
}: AgentManagementStationListProps) {
  const { t } = useTranslation();

  return (
    <section className="overflow-hidden rounded-2xl border border-app-border/80 bg-app-surface shadow-sm">
      <div className="border-b border-app-border/60 bg-app-muted/15 px-5 py-4 sm:px-6">
        <h2 className="text-base font-semibold text-ink">{t('agents.scopeStationsTitle')}</h2>
        <p className="mt-0.5 text-sm text-app-text-dim">
          {t('agents.scopeStationsHint', { org: orgName })}
        </p>
      </div>
      <div className="p-5 sm:p-6">
        {loading ? (
          <p className="text-sm text-app-text-dim">{t('common.loading')}</p>
        ) : stations.length === 0 ? (
          <div className="rounded-xl border border-dashed border-app-border bg-app-muted/10 px-4 py-10 text-center">
            <MapPinIcon className="mx-auto h-8 w-8 text-app-text-dim/50" />
            <p className="mt-3 text-sm text-app-text-sec">{t('agents.scopeNoStations')}</p>
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {stations.map((s) => {
              const rowOrg = String((s as Station & { org_name?: string }).org_name ?? orgName).trim();
              const label = s.name || s.id;
              const selected = s.id === stationId;
              const agentCount =
                typeof s.device_count === 'number' && s.device_count > 0 ? s.device_count : null;

              return (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => onStationSelect(s.id)}
                    className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${
                      selected
                        ? 'border-app-accent/50 bg-app-accent/10 ring-1 ring-app-accent/30'
                        : 'border-app-border bg-app-bg/60 hover:border-app-accent/30 hover:bg-app-accent/5'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-ink">{label}</p>
                        <p className="mt-0.5 truncate text-xs text-app-text-dim">
                          {rowOrg}
                          {s.id !== label ? ` · ${s.id}` : ''}
                        </p>
                      </div>
                      {agentCount != null ? (
                        <span className="shrink-0 rounded-full bg-app-muted/40 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-app-text-dim">
                          {agentCount}
                        </span>
                      ) : null}
                    </div>
                    {selected ? (
                      <p className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-app-accent">
                        {t('agents.scopeSelectedStation')}
                      </p>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
